/**
 * Service de stockage des PDFs de factures dans Firebase Storage
 *
 * Ce service gère le téléversement, la récupération et la suppression
 * des PDFs de factures dans Firebase Storage
 */

import { storage } from '../firebase';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

/**
 * Upload un PDF de facture vers Firebase Storage
 * @param {Blob} pdfBlob - Le blob du PDF à uploader
 * @param {string} invoiceId - L'ID de la facture (ex: FACT-2025-10-0001)
 * @returns {Promise<Object>} - { url, path } URL publique et chemin dans Storage
 */
export const uploadInvoicePDF = async (pdfBlob, invoiceId) => {
  try {
    console.log('📤 Upload PDF pour facture:', invoiceId);

    // Créer la référence vers Storage
    const path = `invoices/${invoiceId}.pdf`;
    const storageRef = ref(storage, path);

    // Upload le fichier
    const snapshot = await uploadBytes(storageRef, pdfBlob, {
      contentType: 'application/pdf',
      customMetadata: {
        invoiceId: invoiceId,
        uploadedAt: new Date().toISOString()
      }
    });

    console.log('📤 Fichier uploadé:', snapshot.metadata.fullPath);

    // Obtenir l'URL publique
    const downloadUrl = await getDownloadURL(storageRef);

    console.log('✅ PDF uploadé avec succès:', downloadUrl);

    return {
      url: downloadUrl,
      path: path,
      size: snapshot.metadata.size
    };
  } catch (error) {
    console.error('❌ Erreur upload PDF:', error);
    throw new Error(`Échec de l'upload du PDF: ${error.message}`);
  }
};

/**
 * Récupère l'URL de téléchargement d'un PDF de facture
 * @param {string} invoiceId - L'ID de la facture
 * @returns {Promise<string>} - URL de téléchargement
 */
export const getInvoicePDFUrl = async (invoiceId) => {
  try {
    const path = `invoices/${invoiceId}.pdf`;
    const storageRef = ref(storage, path);
    const url = await getDownloadURL(storageRef);
    return url;
  } catch (error) {
    if (error.code === 'storage/object-not-found') {
      console.warn('⚠️ PDF non trouvé pour facture:', invoiceId);
      return null;
    }
    console.error('❌ Erreur récupération URL PDF:', error);
    throw error;
  }
};

/**
 * Supprime un PDF de facture de Firebase Storage
 * @param {string} invoiceId - L'ID de la facture
 * @returns {Promise<void>}
 */
export const deleteInvoicePDF = async (invoiceId) => {
  try {
    console.log('🗑️ Suppression PDF pour facture:', invoiceId);

    const path = `invoices/${invoiceId}.pdf`;
    const storageRef = ref(storage, path);

    await deleteObject(storageRef);

    console.log('✅ PDF supprimé avec succès');
  } catch (error) {
    if (error.code === 'storage/object-not-found') {
      console.warn('⚠️ PDF déjà supprimé ou inexistant:', invoiceId);
      return;
    }
    console.error('❌ Erreur suppression PDF:', error);
    throw error;
  }
};

/**
 * Vérifie si un PDF existe dans Storage
 * @param {string} invoiceId - L'ID de la facture
 * @returns {Promise<boolean>}
 */
export const invoicePDFExists = async (invoiceId) => {
  try {
    const url = await getInvoicePDFUrl(invoiceId);
    return url !== null;
  } catch (error) {
    return false;
  }
};

export default {
  uploadInvoicePDF,
  getInvoicePDFUrl,
  deleteInvoicePDF,
  invoicePDFExists
};
